import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { resolveBackendBase } from '../lib/api';

const API_URL = `${resolveBackendBase()}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(localStorage.getItem('token'));

    const api = axios.create({
        baseURL: API_URL,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
    });

    api.interceptors.request.use((config) => {
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
            config.headers.Authorization = `Bearer ${currentToken}`;
        }
        return config;
    });

    api.interceptors.response.use(
        (resp) => resp,
        (err) => {
            // Surface session-expired clearly. Skip the public token-gated assessment routes
            // (they use no auth and shouldn't be redirected to /login on a 401).
            const status = err?.response?.status;
            const url = err?.config?.url || '';
            const isPublic = url.includes('/assessment/') || url.includes('/review/') || url.includes('/auth/login');
            if (status === 401 && !isPublic && localStorage.getItem('token')) {
                localStorage.removeItem('token');
                localStorage.removeItem('auth_version');
                if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
                    // Soft toast cue + redirect. Avoid hard-redirect during AuthContext bootstrap.
                    try { window.dispatchEvent(new CustomEvent('realloc:session-expired')); } catch (_) { /* noop */ }
                }
            }
            return Promise.reject(err);
        }
    );

    const fetchUser = useCallback(async () => {
        const storedToken = localStorage.getItem('token');
        if (!storedToken) {
            setLoading(false);
            return;
        }
        const authVersion = localStorage.getItem('auth_version') || 'v1';
        try {
            const meEndpoint = authVersion === 'v2' ? `${API_URL}/v2/auth/me` : `${API_URL}/auth/me`;
            const response = await axios.get(meEndpoint, {
                headers: { Authorization: `Bearer ${storedToken}` }
            });
            setUser(response.data);
        } catch (error) {
            // Try the other version as fallback
            try {
                const fallback = authVersion === 'v2' ? `${API_URL}/auth/me` : `${API_URL}/v2/auth/me`;
                const response = await axios.get(fallback, {
                    headers: { Authorization: `Bearer ${storedToken}` }
                });
                setUser(response.data);
                localStorage.setItem('auth_version', authVersion === 'v2' ? 'v1' : 'v2');
            } catch {
                localStorage.removeItem('token');
                localStorage.removeItem('auth_version');
                setToken(null);
                setUser(null);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const login = async (email, password) => {
        // Try v2 (PostgreSQL) auth first, fall back to v1 (MongoDB)
        try {
            const response = await axios.post(`${API_URL}/v2/auth/login`, { email, password });
            const { token: newToken, user: userData } = response.data;
            localStorage.setItem('token', newToken);
            localStorage.setItem('auth_version', 'v2');
            setToken(newToken);
            setUser(userData);
            return userData;
        } catch (v2Error) {
            // Fall back to v1
            const response = await axios.post(`${API_URL}/auth/login`, { email, password });
            const { token: newToken, user: userData } = response.data;
            localStorage.setItem('token', newToken);
            localStorage.setItem('auth_version', 'v1');
            setToken(newToken);
            setUser(userData);
            return userData;
        }
    };

    const register = async (name, email, password, accessCode) => {
        const response = await axios.post(`${API_URL}/auth/register`, {
            name, email, password,
            enterprise_access_code: accessCode || undefined
        });
        const { token: newToken, user: userData } = response.data;
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(userData);
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
    };

    const refreshUser = async () => {
        await fetchUser();
    };

    const swapToken = async (newToken) => {
        // Used for impersonation / "View as user" — replaces the active session token.
        localStorage.setItem('token', newToken);
        localStorage.setItem('auth_version', 'v2');
        setToken(newToken);
        try {
            const res = await axios.get(`${API_URL}/v2/auth/me`, {
                headers: { Authorization: `Bearer ${newToken}` },
            });
            setUser(res.data);
        } catch (e) { /* no-op; token-change effect will retry */ }
    };

    const value = {
        user, loading, login, register, logout, api, swapToken,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'super_admin' || user?.role === 'platform_admin',
        isPlatformAdmin: user?.role === 'platform_admin',
        isEnterprise: user?.role === 'enterprise_admin' || user?.role === 'super_admin' || user?.role === 'enterprise_executive',
        isMentor: user?.role === 'mentor',
        isParticipant: user?.role === 'participant' || user?.role === 'employee',
        refreshUser
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
